import { useQuery } from '@tanstack/react-query';
import { CalendarDays, FileText, Lock, Mail, MessageSquare, Star, UserPlus } from 'lucide-react';
import { Link } from 'react-router';

import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { api } from '@/lib/api';
import { formatInstant } from '@/lib/dates';

/*
 * The customer timeline — ROADMAP.md §9.7.
 *
 * **A read across the existing tables, never a table of its own.** A timeline
 * table would be a second copy of history that every write path has to
 * remember to update, and the day somebody adds a path and forgets it, the
 * timeline is quietly missing something nobody notices. This is slower and
 * cannot drift.
 */

interface TimelineEntry {
  at: string;
  kind: 'enquiry' | 'became_client' | 'message' | 'note' | 'job' | 'invoice' | 'email';
  description: string;
  href?: string;
}

const ICONS = {
  enquiry: Star,
  became_client: UserPlus,
  message: MessageSquare,
  note: Lock,
  job: CalendarDays,
  // The same icon /admin/invoices uses for an invoice, so the two screens agree.
  invoice: FileText,
  email: Mail,
} as const;

export function CustomerTimeline({ clientId }: { clientId: string }) {
  const query = useQuery({
    queryKey: ['admin', 'clients', clientId, 'timeline'],
    queryFn: () => api.get<{ entries: TimelineEntry[] }>(`/api/admin/clients/${clientId}/timeline`),
  });

  if (query.isPending) {
    return <div className="h-24 animate-pulse rounded-lg bg-ink-100" aria-busy="true" />;
  }

  // A failed request is an error, never an empty state — CLAUDE.md §4 (P22).
  if (query.isError) {
    return (
      <ErrorState title="Their history could not be loaded" onRetry={() => void query.refetch()} />
    );
  }

  if (query.data.entries.length === 0) {
    return (
      <EmptyState
        title="Nothing has happened yet"
        hint="Jobs, messages and invoices appear here."
      />
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {query.data.entries.map((entry, index) => {
        const Icon = ICONS[entry.kind];

        return (
          <li key={`${entry.at}-${String(index)}`} className="flex items-start gap-3">
            <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full bg-honey-50">
              <Icon aria-hidden="true" className="size-4 text-bark-700" />
            </span>
            <div className="min-w-0">
              <p className="text-body text-ink-900">
                {entry.href === undefined ? (
                  entry.description
                ) : (
                  <Link to={entry.href} className="text-bark-700 underline underline-offset-2">
                    {entry.description}
                  </Link>
                )}
              </p>
              <p className="text-caption text-ink-500">{formatInstant(entry.at)}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
